import type { Ordering } from "@/types/base";
import type { ColumnFilters, ColumnFilterValue, DateFilter } from "@/modules/snapshots/grid/sortAndFilter";
import type { SnapshotGridField } from "@/modules/snapshots/businessReports.api";
import { snapshotGridColumnKey } from "@/modules/snapshots/grid/gridColumnKeys";

export interface SnapshotGridQueryPayload {
  offset: number;
  limit: number;
  query: string;
  subcollection_id?: string;
  contract_family_id?: string;
  supplier_contract_folder_id?: string;
  ordering: Ordering;
  column_filters: Record<string, unknown>;
}

interface BuildSnapshotGridQueryPayloadParams {
  offset: number;
  limit: number;
  query: string;
  ordering: Ordering;
  subcollectionId?: string;
  contractFamilyId?: string;
  supplierContractFolderId?: string;
  columnFilters: ColumnFilters;
  dateFilter: DateFilter;
  fields?: SnapshotGridField[];
}

const toWireColumnFilter = (value: ColumnFilterValue): Record<string, unknown> => {
  const { reviewVerdicts, ...rest } = value;
  return {
    ...rest,
    ...(reviewVerdicts && reviewVerdicts.length > 0 ? { review_verdicts: reviewVerdicts } : {}),
  };
};

export const formatSnapshotGridLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildSnapshotGridQueryPayload = ({
  offset,
  limit,
  query,
  ordering,
  subcollectionId,
  contractFamilyId,
  supplierContractFolderId,
  columnFilters,
  dateFilter,
  fields = [],
}: BuildSnapshotGridQueryPayloadParams): SnapshotGridQueryPayload => {
  const nextFilters: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(columnFilters)) {
    const key = snapshotGridColumnKey(name, fields);
    if (key && value) nextFilters[key] = toWireColumnFilter(value);
  }

  if (dateFilter.selectedField && (dateFilter.startDate || dateFilter.endDate)) {
    const dateFieldKey = snapshotGridColumnKey(dateFilter.selectedField, fields);
    if (dateFieldKey)
      nextFilters[dateFieldKey] = {
        type: "date",
        startDate: dateFilter.startDate ? formatSnapshotGridLocalDate(dateFilter.startDate) : null,
        endDate: dateFilter.endDate ? formatSnapshotGridLocalDate(dateFilter.endDate) : null,
        excludeNA: false,
      };
  }

  return {
    offset,
    limit,
    query,
    ...(subcollectionId ? { subcollection_id: subcollectionId } : {}),
    ...(contractFamilyId ? { contract_family_id: contractFamilyId } : {}),
    ...(supplierContractFolderId ? { supplier_contract_folder_id: supplierContractFolderId } : {}),
    ordering: {
      ...ordering,
      order_by: snapshotGridColumnKey(ordering.order_by, fields) ?? "system:status",
    },
    column_filters: nextFilters,
  };
};
